// src/components/purchases/PurchaseInvoiceForm.js
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Paper,
  Divider,
  useTheme,
  useMediaQuery,
  Autocomplete,
  Dialog,
  DialogContent,
  Checkbox,
  OutlinedInput,
  ListItemText,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { Close, Save, Add, Delete, AttachFile } from "@mui/icons-material";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import { useCollection } from "../../hooks/useCollection";
import {
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  EMITTER_TYPES,
  EMITTER_TYPE_LABELS,
} from "../../utils/models/purchaseInvoiceModel";
import { CONTACT_TYPES } from "../../utils/models/contactModel";
import ContactForm from "../contacts/ContactForm";
import CommercialForm from "../commercials/CommercialForm";
import ClientForm from "../clients/ClientForm";

const PurchaseInvoiceForm = ({ invoice, onSave, onCancel }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { add: addInvoice, update: updateInvoice } =
    useAuditFirestore("purchaseInvoices");
  const { data: contacts, refresh: refreshContacts } =
    useCollection("contacts");
  const { data: commercials, refresh: refreshCommercials } =
    useCollection("commercials");
  const { data: clients, refresh: refreshClients } = useCollection("clients");
  const { data: projects } = useCollection("projects");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const [commercialDialogOpen, setCommercialDialogOpen] = useState(false);
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const editingTotalRef = useRef(false);

  const [formData, setFormData] = useState({
    invoiceNumber: invoice?.invoiceNumber || "",

    // Compatibilidad con datos antiguos (supplier*) y nuevos (emitter*)
    emitterType: invoice?.emitterType || EMITTER_TYPES.SUPPLIER,
    emitterId: invoice?.emitterId || invoice?.supplierId || "",
    emitterName: invoice?.emitterName || invoice?.supplierName || "",
    emitterTaxId: invoice?.emitterTaxId || invoice?.supplierTaxId || "",
    emitterAddress: invoice?.emitterAddress || invoice?.supplierAddress || "",

    receiverName: invoice?.receiverName || "EPC Development & Management, S.L.",
    receiverTaxId: invoice?.receiverTaxId || "B26623850",
    receiverAddress:
      invoice?.receiverAddress || "C/ Juan de Austria 13, Cox, Alicante",
    baseAmount: invoice?.baseAmount || 0,
    taxRate: invoice?.taxRate || 21,
    taxAmount: invoice?.taxAmount || 0,
    total: invoice?.total || 0,
    projects: invoice?.projects || [],
    allocationPercentage: invoice?.allocationPercentage || "",
    issueDate: invoice?.issueDate
      ? invoice.issueDate.split("T")[0]
      : new Date().toISOString().split("T")[0],
    dueDate: invoice?.dueDate ? invoice.dueDate.split("T")[0] : "",
    paymentMethod: invoice?.paymentMethod || PAYMENT_METHODS.TRANSFER,
    iban: invoice?.iban || "",
    status: invoice?.status || "unpaid",
    notes: invoice?.notes || "",
    files: invoice?.files || [], // NUEVO: archivos
  });

    // Nombres para ordenar y mostrar
  const getSupplierName = (c) =>
    c?.companyName || `${c?.firstName || ""} ${c?.lastName || ""}`.trim() || "";

  const getCommercialName = (c) =>
    `${c?.firstName || ""} ${c?.lastName || ""}`.trim() || "";

  const getClientName = (c) =>
    c?.companyName || `${c?.firstName || ""} ${c?.lastName || ""}`.trim() || "";

  // Filtrar contactos proveedor, activos y ordenados alfabéticamente
  const supplierContacts = React.useMemo(() => {
    const list =
      (contacts || []).filter(
        (c) =>
          c.contactType === CONTACT_TYPES.SUPPLIER && c.isActive !== false,
      );
    return [...list].sort((a, b) =>
      getSupplierName(a).localeCompare(getSupplierName(b), "es", {
        sensitivity: "base",
      }),
    );
  }, [contacts]);

  // Comerciales activos ordenados
  const activeCommercials = React.useMemo(() => {
    const list = (commercials || []).filter((c) => c.isActive !== false);
    return [...list].sort((a, b) =>
      getCommercialName(a).localeCompare(getCommercialName(b), "es", {
        sensitivity: "base",
      }),
    );
  }, [commercials]);

  // Clientes activos ordenados
  const activeClients = React.useMemo(() => {
    const list = (clients || []).filter((c) => c.isActive !== false);
    return [...list].sort((a, b) =>
      getClientName(a).localeCompare(getClientName(b), "es", {
        sensitivity: "base",
      }),
    );
  }, [clients]);

  useEffect(() => {
    const base = parseFloat(formData.baseAmount) || 0;
    const total = parseFloat(formData.total) || 0;
    const rate = parseFloat(formData.taxRate) || 21;

    // Si está editando Total manualmente, no tocamos Base
    if (editingTotalRef.current) {
      if (total > 0) {
        const calculatedBase = total / (1 + rate / 100);
        const calculatedTax = total - calculatedBase;
        setFormData((prev) => ({
          ...prev,
          baseAmount: parseFloat(calculatedBase.toFixed(2)),
          taxAmount: parseFloat(calculatedTax.toFixed(2)),
        }));
      }
      editingTotalRef.current = false;
      return;
    }

    // Si hay base imponible, calcular IVA y Total
    if (base > 0) {
      const calculatedTax = base * (rate / 100);
      const calculatedTotal = base + calculatedTax;
      setFormData((prev) => ({
        ...prev,
        taxAmount: parseFloat(calculatedTax.toFixed(2)),
        total: parseFloat(calculatedTotal.toFixed(2)),
      }));
    }
  }, [formData.baseAmount, formData.taxRate, formData.total]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError("");
  };
  const handleTotalChange = (e) => {
    editingTotalRef.current = true;
    handleChange(e);
  };

  const handleEmitterTypeChange = (event, newType) => {
    if (newType !== null) {
      setFormData((prev) => ({
        ...prev,
        emitterType: newType,
        emitterId: "",
        emitterName: "",
        emitterTaxId: "",
        emitterAddress: "",
      }));
    }
  };

  const handleSupplierSelect = (selectedItem) => {
    if (!selectedItem) {
      setFormData((prev) => ({
        ...prev,
        emitterId: "",
        emitterName: "",
        emitterTaxId: "",
        emitterAddress: "",
      }));
      return;
    }
    const name =
      selectedItem.companyName ||
      `${selectedItem.firstName} ${selectedItem.lastName}`;
    const taxId = selectedItem.companyTaxId || selectedItem.dni || "";
    const address = selectedItem.address?.street
      ? `${selectedItem.address.street}, ${selectedItem.address.city}`
      : "";
    setFormData((prev) => ({
      ...prev,
      emitterId: selectedItem.id,
      emitterName: name,
      emitterTaxId: taxId,
      emitterAddress: address,
    }));
  };

  const handleCommercialSelect = (selectedItem) => {
    if (!selectedItem) {
      setFormData((prev) => ({
        ...prev,
        emitterId: "",
        emitterName: "",
        emitterTaxId: "",
        emitterAddress: "",
      }));
      return;
    }
    const name = `${selectedItem.firstName} ${selectedItem.lastName}`;
    const taxId = selectedItem.dni || "";
    const address = selectedItem.companyAddress?.street
      ? `${selectedItem.companyAddress.street}, ${selectedItem.companyAddress.city}`
      : "";
    setFormData((prev) => ({
      ...prev,
      emitterId: selectedItem.id,
      emitterName: name,
      emitterTaxId: taxId,
      emitterAddress: address,
    }));
  };

  // NUEVO: seleccionar cliente como emisor (para ABONOS)
  const handleClientSelect = (selectedItem) => {
    if (!selectedItem) {
      setFormData((prev) => ({
        ...prev,
        emitterId: "",
        emitterName: "",
        emitterTaxId: "",
        emitterAddress: "",
      }));
      return;
    }
    const name =
      selectedItem.companyName ||
      `${selectedItem.firstName || ""} ${selectedItem.lastName || ""}`.trim();
    const taxId =
      selectedItem.taxId || selectedItem.companyTaxId || selectedItem.dni || "";
    const address = selectedItem.address?.street
      ? `${selectedItem.address.street}, ${selectedItem.address.city}`
      : "";
    setFormData((prev) => ({
      ...prev,
      emitterId: selectedItem.id,
      emitterName: name,
      emitterTaxId: taxId,
      emitterAddress: address,
    }));
  };

  const handleProjectsChange = (event) => {
    const selectedProjectIds = event.target.value;
    setFormData((prev) => {
      const existingProjects = prev.projects.filter((p) =>
        selectedProjectIds.includes(p.projectId),
      );
      const newProjects = selectedProjectIds
        .filter((id) => !prev.projects.some((p) => p.projectId === id))
        .map((id) => ({ projectId: id, amount: 0 }));
      return { ...prev, projects: [...existingProjects, ...newProjects] };
    });
  };

  const handleProjectAmountChange = (projectId, amount) => {
    setFormData((prev) => ({
      ...prev,
      projects: prev.projects.map((p) =>
        p.projectId === projectId
          ? { ...p, amount: parseFloat(amount) || 0 }
          : p,
      ),
    }));
  };

  // NUEVO: Manejar archivos
  const handleFileChange = (index, field, value) => {
    const newFiles = [...formData.files];
    newFiles[index] = { ...newFiles[index], [field]: value };
    setFormData((prev) => ({ ...prev, files: newFiles }));
  };

  const handleAddFile = () => {
    setFormData((prev) => ({
      ...prev,
      files: [...prev.files, { name: "", url: "" }],
    }));
  };

  const handleRemoveFile = (index) => {
    const newFiles = formData.files.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, files: newFiles }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.invoiceNumber) {
      setError("El número de factura es obligatorio");
      return;
    }
    if (!formData.emitterId) {
      setError("Selecciona un emisor (proveedor, comercial o cliente)");
      return;
    }
    setLoading(true);
    setError("");
    try {
      // Filtrar archivos vacíos
      const validFiles = formData.files.filter(
        (file) => file.name.trim() !== "" && file.url.trim() !== "",
      );

      const invoiceData = {
        ...formData,
        files: validFiles,
        updatedAt: new Date().toISOString(),
      };

      if (invoice?.id) {
        await updateInvoice(invoice.id, invoiceData);
      } else {
        await addInvoice({
          ...invoiceData,
          createdAt: new Date().toISOString(),
          createdBy: "",
          isActive: true,
        });
      }
      onSave();
    } catch (err) {
      console.error(err);
      setError(err.message || "Error al guardar");
    } finally {
      setLoading(false);
    }
  };

  const formatSupplierOption = (option) => {
    if (!option) return "";
    return (
      option.companyName ||
      `${option.firstName || ""} ${option.lastName || ""}`.trim()
    );
  };

  const formatCommercialOption = (option) => {
    if (!option) return "";
    return `${option.firstName || ""} ${option.lastName || ""}`.trim();
  };

  const formatClientOption = (option) => {
    if (!option) return "";
    return (
      option.companyName ||
      `${option.firstName || ""} ${option.lastName || ""}`.trim()
    );
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme.palette.background.paper,
      height: { xs: 40, sm: 44 },
    },
    "& .MuiInputLabel-root": {
      backgroundColor: theme.palette.background.paper,
      paddingInline: "4px",
      fontSize: { xs: "0.8rem", sm: "0.875rem" },
    },
  };

  const sectionTitleSx = {
    fontWeight: 700,
    fontSize: { xs: 14, sm: 15 },
    mb: { xs: 1.5, sm: 2 },
    mt: { xs: 4, sm: 5 },
    color: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    gap: 1,
  };

  const activeProjects = projects?.filter((p) => p.isActive !== false) || [];
  const selectedProjectIds = formData.projects.map((p) => p.projectId);

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: { xs: "16px", sm: "24px" },
          border: "1px solid",
          borderColor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.12)"
              : "#e2e8f0",
          background: theme.palette.background.paper,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: { xs: 3, sm: 4 },
          }}
        >
          <Box>
            <Typography
              variant="h5"
              fontWeight={800}
              sx={{
                fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" },
                color: theme.palette.primary.main,
              }}
            >
              {invoice?.id
                ? "Editar factura de compra"
                : "Nueva factura de compra"}
            </Typography>
          </Box>
          <IconButton
            onClick={onCancel}
            size="small"
            sx={{
              background: theme.palette.background.paper,
              border: "1px solid",
              borderColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.12)"
                  : "#e2e8f0",
            }}
          >
            <Close sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: { xs: 2, sm: 3 },
              borderRadius: "12px",
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
            }}
          >
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Typography sx={sectionTitleSx}>Datos de la factura</Typography>
          <Box
            sx={{
              display: "flex",
              gap: { xs: 1.5, sm: 2.5 },
              flexWrap: "wrap",
              width: "100%",
            }}
          >
            <TextField
              fullWidth
              size="small"
              label="Nº Factura"
              name="invoiceNumber"
              value={formData.invoiceNumber}
              onChange={handleChange}
              sx={{
                ...fieldSx,
                flex: 1,
                minWidth: { xs: "100%", sm: "200px" },
              }}
              required
              inputProps={{
                style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="Fecha emisión"
              name="issueDate"
              type="date"
              value={formData.issueDate}
              onChange={handleChange}
              sx={{
                ...fieldSx,
                flex: 1,
                minWidth: { xs: "100%", sm: "200px" },
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="Fecha vencimiento"
              name="dueDate"
              type="date"
              value={formData.dueDate}
              onChange={handleChange}
              sx={{
                ...fieldSx,
                flex: 1,
                minWidth: { xs: "100%", sm: "200px" },
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
              }}
            />
          </Box>

          <Typography sx={sectionTitleSx}>Emisor</Typography>

          {/* Selector de tipo de emisor */}
          <ToggleButtonGroup
            value={formData.emitterType}
            exclusive
            onChange={handleEmitterTypeChange}
            size="small"
            sx={{ mb: 2, flexWrap: "wrap", gap: 0.5 }}
          >
            <ToggleButton
              value={EMITTER_TYPES.SUPPLIER}
              sx={{ borderRadius: "12px !important", textTransform: "none" }}
            >
              {EMITTER_TYPE_LABELS[EMITTER_TYPES.SUPPLIER]}
            </ToggleButton>
            <ToggleButton
              value={EMITTER_TYPES.COMMERCIAL}
              sx={{ borderRadius: "12px !important", textTransform: "none" }}
            >
              {EMITTER_TYPE_LABELS[EMITTER_TYPES.COMMERCIAL]}
            </ToggleButton>
            <ToggleButton
              value={EMITTER_TYPES.CLIENT}
              sx={{ borderRadius: "12px !important", textTransform: "none" }}
            >
              {EMITTER_TYPE_LABELS[EMITTER_TYPES.CLIENT]}
            </ToggleButton>
          </ToggleButtonGroup>

          {formData.emitterType === EMITTER_TYPES.SUPPLIER && (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Autocomplete
                options={supplierContacts}
                getOptionLabel={formatSupplierOption}
                value={
                  supplierContacts.find((c) => c.id === formData.emitterId) ||
                  null
                }
                onChange={(_, value) => handleSupplierSelect(value)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Buscar proveedor"
                    size="small"
                    sx={{ ...fieldSx, minWidth: 250 }}
                  />
                )}
                sx={{ flex: 2, minWidth: 200 }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => setSupplierDialogOpen(true)}
                sx={{
                  borderRadius: "12px",
                  height: 44,
                  textTransform: "none",
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                }}
              >
                + Nuevo Proveedor
              </Button>
            </Box>
          )}

          {formData.emitterType === EMITTER_TYPES.COMMERCIAL && (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Autocomplete
                options={activeCommercials}
                getOptionLabel={formatCommercialOption}
                value={
                  activeCommercials.find((c) => c.id === formData.emitterId) ||
                  null
                }
                onChange={(_, value) => handleCommercialSelect(value)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Buscar comercial"
                    size="small"
                    sx={{ ...fieldSx, minWidth: 250 }}
                  />
                )}
                sx={{ flex: 2, minWidth: 200 }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => setCommercialDialogOpen(true)}
                sx={{
                  borderRadius: "12px",
                  height: 44,
                  textTransform: "none",
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                }}
              >
                + Nuevo Comercial
              </Button>
            </Box>
          )}

          {formData.emitterType === EMITTER_TYPES.CLIENT && (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Autocomplete
                options={activeClients}
                getOptionLabel={formatClientOption}
                value={
                  activeClients.find((c) => c.id === formData.emitterId) || null
                }
                onChange={(_, value) => handleClientSelect(value)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Buscar cliente"
                    size="small"
                    sx={{ ...fieldSx, minWidth: 250 }}
                  />
                )}
                sx={{ flex: 2, minWidth: 200 }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => setClientDialogOpen(true)}
                sx={{
                  borderRadius: "12px",
                  height: 44,
                  textTransform: "none",
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                }}
              >
                + Nuevo Cliente
              </Button>
            </Box>
          )}

          {formData.emitterId && (
            <Box
              sx={{
                display: "flex",
                gap: { xs: 1.5, sm: 2.5 },
                flexWrap: "wrap",
                width: "100%",
                mt: 2,
              }}
            >
              <TextField
                fullWidth
                size="small"
                label="CIF/NIF"
                value={formData.emitterTaxId}
                sx={{
                  ...fieldSx,
                  flex: 1,
                  minWidth: { xs: "100%", sm: "200px" },
                }}
                InputProps={{ readOnly: true }}
              />
              <TextField
                fullWidth
                size="small"
                label="Dirección"
                value={formData.emitterAddress}
                sx={{
                  ...fieldSx,
                  flex: 2,
                  minWidth: { xs: "100%", sm: "300px" },
                }}
                InputProps={{ readOnly: true }}
              />
            </Box>
          )}

          <Typography sx={sectionTitleSx}>Importes</Typography>
          <Box
            sx={{
              display: "flex",
              gap: { xs: 1.5, sm: 2.5 },
              flexWrap: "wrap",
              width: "100%",
            }}
          >
            <TextField
              fullWidth
              size="small"
              label="Base imponible"
              name="baseAmount"
              type="number"
              value={formData.baseAmount}
              onChange={handleChange}
              sx={{
                ...fieldSx,
                flex: 1,
                minWidth: { xs: "100%", sm: "150px" },
              }}
              inputProps={{
                style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="IVA (%)"
              name="taxRate"
              type="number"
              value={formData.taxRate}
              onChange={handleChange}
              sx={{
                ...fieldSx,
                flex: 1,
                minWidth: { xs: "100%", sm: "120px" },
              }}
              inputProps={{
                min: 0,
                max: 100,
                style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="IVA (€)"
              value={(parseFloat(formData.taxAmount) || 0).toFixed(2)}
              sx={{
                ...fieldSx,
                flex: 1,
                minWidth: { xs: "100%", sm: "150px" },
              }}
              InputProps={{ readOnly: true }}
            />
            <TextField
              fullWidth
              size="small"
              label="Total"
              name="total"
              type="number"
              value={formData.total || ""}
              onChange={handleTotalChange}
              sx={{
                ...fieldSx,
                flex: 1,
                minWidth: { xs: "100%", sm: "150px" },
              }}
              inputProps={{
                step: "0.01",
                style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
              }}
            />
          </Box>

          <Typography sx={sectionTitleSx}>Proyectos asociados</Typography>
          <FormControl fullWidth size="small" sx={fieldSx}>
            <InputLabel>Seleccionar proyectos</InputLabel>
            <Select
              multiple
              value={selectedProjectIds}
              onChange={handleProjectsChange}
              input={<OutlinedInput label="Seleccionar proyectos" />}
              renderValue={(selected) =>
                selected
                  .map(
                    (id) => activeProjects.find((p) => p.id === id)?.name || id,
                  )
                  .join(", ")
              }
              MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
            >
              {activeProjects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  <Checkbox
                    checked={selectedProjectIds.indexOf(project.id) > -1}
                  />
                  <ListItemText primary={project.name || project.title} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {formData.projects.map((projectAssignment) => {
            const project = activeProjects.find(
              (p) => p.id === projectAssignment.projectId,
            );
            return (
              <Box
                key={projectAssignment.projectId}
                sx={{ display: "flex", gap: 2, alignItems: "center", mt: 1 }}
              >
                <Typography
                  variant="body2"
                  sx={{ flex: 1, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
                >
                  {project?.name || project?.title || "Proyecto"}
                </Typography>
                <TextField
                  size="small"
                  label="Importe (€)"
                  type="number"
                  value={projectAssignment.amount || 0}
                  onChange={(e) =>
                    handleProjectAmountChange(
                      projectAssignment.projectId,
                      e.target.value,
                    )
                  }
                  sx={{ ...fieldSx, width: 150 }}
                  inputProps={{
                    style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                  }}
                />
              </Box>
            );
          })}

          {formData.emitterId && (
            <>
              <Typography sx={sectionTitleSx}>Asignación a EPC</Typography>
              <TextField
                fullWidth
                size="small"
                label="% Asignación"
                name="allocationPercentage"
                value={formData.allocationPercentage}
                onChange={handleChange}
                sx={fieldSx}
              />
            </>
          )}

          <Typography sx={sectionTitleSx}>Pago</Typography>
          <Box
            sx={{
              display: "flex",
              gap: { xs: 1.5, sm: 2.5 },
              flexWrap: "wrap",
              width: "100%",
            }}
          >
            <TextField
              fullWidth
              size="small"
              select
              label="Forma de pago"
              name="paymentMethod"
              value={formData.paymentMethod}
              onChange={handleChange}
              sx={{
                ...fieldSx,
                flex: 1,
                minWidth: { xs: "100%", sm: "200px" },
              }}
            >
              {Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
            {formData.paymentMethod === "transfer" && (
              <TextField
                fullWidth
                size="small"
                label="IBAN"
                name="iban"
                value={formData.iban}
                onChange={handleChange}
                sx={{
                  ...fieldSx,
                  flex: 2,
                  minWidth: { xs: "100%", sm: "250px" },
                }}
              />
            )}
          </Box>

          {/* NUEVO: SECCIÓN DE ARCHIVOS */}
          <Typography sx={sectionTitleSx}>
            <AttachFile sx={{ fontSize: { xs: 18, sm: 20 } }} />
            Archivos
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              width: "100%",
            }}
          >
            {formData.files.map((file, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  gap: { xs: 1, sm: 1.5 },
                  alignItems: "center",
                  width: "100%",
                  flexWrap: { xs: "wrap", sm: "nowrap" },
                }}
              >
                <TextField
                  size="small"
                  label="Nombre del archivo"
                  value={file.name}
                  onChange={(e) =>
                    handleFileChange(index, "name", e.target.value)
                  }
                  sx={{
                    ...fieldSx,
                    flex: 1,
                    minWidth: { xs: "100%", sm: "200px" },
                  }}
                  inputProps={{
                    style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                  }}
                />
                <TextField
                  size="small"
                  label="Enlace (URL)"
                  value={file.url}
                  onChange={(e) =>
                    handleFileChange(index, "url", e.target.value)
                  }
                  sx={{
                    ...fieldSx,
                    flex: 2,
                    minWidth: { xs: "100%", sm: "300px" },
                  }}
                  inputProps={{
                    style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } },
                  }}
                />
                <IconButton
                  onClick={() => handleRemoveFile(index)}
                  size="small"
                  sx={{
                    width: { xs: 40, sm: 44 },
                    height: { xs: 40, sm: 44 },
                    borderRadius: "12px",
                    border: "1px solid",
                    borderColor: theme.palette.error.main,
                    color: theme.palette.error.main,
                    flexShrink: 0,
                    "&:hover": {
                      backgroundColor: theme.palette.error.main,
                      color: "white",
                    },
                  }}
                >
                  <Delete sx={{ fontSize: { xs: 18, sm: 20 } }} />
                </IconButton>
              </Box>
            ))}

            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={handleAddFile}
              sx={{
                borderRadius: "12px",
                height: { xs: 40, sm: 44 },
                textTransform: "none",
                fontSize: { xs: "0.8rem", sm: "0.85rem" },
                alignSelf: "flex-start",
                mt: 1,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                "&:hover": {
                  borderColor: theme.palette.primary.dark,
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(0, 175, 163, 0.1)"
                      : "rgba(0, 95, 101, 0.05)",
                },
              }}
            >
              Añadir archivo
            </Button>
          </Box>

          <Typography sx={sectionTitleSx}>Notas</Typography>
          <TextField
            fullWidth
            size="small"
            label="Notas"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            multiline
            rows={2}
            sx={fieldSx}
          />

          <Divider sx={{ mt: { xs: 4, sm: 5 }, mb: { xs: 2, sm: 3 } }} />
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="outlined"
              onClick={onCancel}
              sx={{
                borderRadius: "12px",
                px: { xs: 2, sm: 3 },
                height: { xs: 40, sm: 44 },
                textTransform: "none",
                fontSize: { xs: "0.8rem", sm: "0.85rem" },
              }}
              fullWidth={{ xs: true, sm: false }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              startIcon={<Save />}
              sx={{
                borderRadius: "12px",
                px: { xs: 2, sm: 3 },
                height: { xs: 40, sm: 44 },
                boxShadow: "none",
                textTransform: "none",
                fontSize: { xs: "0.8rem", sm: "0.85rem" },
              }}
              fullWidth={{ xs: true, sm: false }}
            >
              {loading
                ? "Guardando..."
                : invoice?.id
                  ? "Actualizar"
                  : "Crear factura"}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Diálogo para nuevo proveedor */}
      <Dialog
        open={supplierDialogOpen}
        onClose={() => setSupplierDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: "16px", sm: "24px" },
            margin: { xs: 1, sm: 2 },
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <ContactForm
            contact={{ contactType: CONTACT_TYPES.SUPPLIER }}
            onSave={(savedContact) => {
              setSupplierDialogOpen(false);
              if (savedContact) {
                handleSupplierSelect(savedContact);
                if (refreshContacts) refreshContacts();
              }
            }}
            onCancel={() => setSupplierDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Diálogo para nuevo comercial */}
      <Dialog
        open={commercialDialogOpen}
        onClose={() => setCommercialDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: "16px", sm: "24px" },
            margin: { xs: 1, sm: 2 },
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <CommercialForm
            commercial={null}
            onSave={(savedCommercial) => {
              setCommercialDialogOpen(false);
              if (savedCommercial) {
                handleCommercialSelect(savedCommercial);
                if (refreshCommercials) refreshCommercials();
              }
            }}
            onCancel={() => setCommercialDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Diálogo para nuevo cliente */}
      <Dialog
        open={clientDialogOpen}
        onClose={() => setClientDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: "16px", sm: "24px" },
            margin: { xs: 1, sm: 2 },
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <ClientForm
            client={null}
            onSave={(savedClient) => {
              setClientDialogOpen(false);
              if (savedClient) {
                handleClientSelect(savedClient);
                if (refreshClients) refreshClients();
              }
            }}
            onCancel={() => setClientDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PurchaseInvoiceForm;
